import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    
    // Only initialize filtering if we have separate Clay/Glaze dropdowns
    // Skip if there's a combined variant select (single dropdown)
    const hasCombinedSelect = this.querySelector('select[data-combined-variant-select="true"]');
    if (!hasCombinedSelect) {
      // Initialize dynamic option filtering for Clay/Glaze dropdowns
      this.initializeOptionFiltering();
    }
  }

  /**
   * Initializes dynamic filtering of dropdown options based on valid variant combinations.
   */
  initializeOptionFiltering() {
    console.log('=== Initializing option filtering ===');
    const selects = Array.from(this.querySelectorAll('select[data-option-type]'));
    console.log('Found selects with data-option-type:', selects.length);
    if (selects.length < 2) {
      console.warn('Not enough selects found for filtering');
      return;
    }
    
    const claySelect = selects.find(s => s instanceof HTMLSelectElement && s.dataset.optionType === 'clay');
    const glazeSelect = selects.find(s => s instanceof HTMLSelectElement && s.dataset.optionType === 'glaze');
    
    if (!claySelect || !glazeSelect || !(claySelect instanceof HTMLSelectElement) || !(glazeSelect instanceof HTMLSelectElement)) {
      console.warn('Clay or glaze select not found or invalid');
      return;
    }
    
    console.log('Clay select found:', claySelect);
    console.log('Glaze select found:', glazeSelect);
    console.log('Valid combinations data:', claySelect.dataset.validCombinations);
    
    // Parse valid combinations from the first select (they should all have the same data)
    const validCombinationsMap = this.parseValidCombinations(claySelect.dataset.validCombinations || '');
    
    // Store the map for later use
    this.validCombinationsMap = validCombinationsMap;
    
    // Filter glaze options based on initial clay selection
    this.filterGlazeOptions(claySelect, glazeSelect, validCombinationsMap);
    
    // Listen for clay changes to update glaze options
    claySelect.addEventListener('change', (e) => {
      console.log('=== Clay select changed event fired ===');
      console.log('New clay value:', claySelect.value);
      this.filterGlazeOptions(claySelect, glazeSelect, validCombinationsMap);
      // If current glaze selection is invalid, select the first valid one
      const currentGlaze = glazeSelect.value;
      const selectedClay = claySelect.value || '';
      const validGlazes = this.getValidGlazesForClay(selectedClay, validCombinationsMap);
      if (!validGlazes.includes(currentGlaze) && validGlazes.length > 0 && validGlazes[0]) {
        glazeSelect.value = validGlazes[0];
        glazeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /**
   * Parses the valid combinations string into a map.
   * @param {string} combinationsString - The combinations string from data attribute.
   * @returns {Record<string, string[]>} Map of clay -> array of valid glazes.
   */
  parseValidCombinations(combinationsString) {
    /** @type {Record<string, string[]>} */
    const map = {};
    if (!combinationsString) {
      console.warn('No valid combinations string provided');
      return map;
    }
    
    console.log('Parsing combinations string:', combinationsString);
    
    // Format: "Creme Clay:Glaze1|Glaze2|,Dark Clay:Glaze3|Glaze4|"
    const entries = combinationsString.split(',');
    entries.forEach(entry => {
      const [clay, glazes] = entry.split(':');
      if (clay && glazes) {
        // Remove leading/trailing pipes and split
        const glazeArray = glazes.replace(/^\|+|\|+$/g, '').split('|').filter(g => g && g.trim() !== '');
        map[clay.trim()] = glazeArray;
        console.log('Mapped', clay.trim(), 'to glazes:', glazeArray);
      }
    });
    
    console.log('Final parsed map:', map);
    return map;
  }

  /**
   * Gets valid glazes for a given clay.
   * @param {string} clayValue - The selected clay value.
   * @param {Record<string, string[]>} validCombinationsMap - The valid combinations map.
   * @returns {string[]} Array of valid glaze values.
   */
  getValidGlazesForClay(clayValue, validCombinationsMap) {
    return validCombinationsMap[clayValue] || [];
  }

  /**
   * Filters glaze options based on selected clay.
   * @param {HTMLSelectElement} claySelect - The clay select element.
   * @param {HTMLSelectElement} glazeSelect - The glaze select element.
   * @param {Record<string, string[]>} validCombinationsMap - The valid combinations map.
   */
  filterGlazeOptions(claySelect, glazeSelect, validCombinationsMap) {
    const selectedClay = claySelect.value;
    console.log('=== Filtering glazes ===');
    console.log('Selected clay:', selectedClay);
    console.log('Valid combinations map:', validCombinationsMap);
    
    const validGlazes = this.getValidGlazesForClay(selectedClay, validCombinationsMap);
    console.log('Valid glazes for', selectedClay, ':', validGlazes);
    
    // Debug logging (can be removed later)
    if (validGlazes.length === 0) {
      console.warn('No valid glazes found for clay:', selectedClay);
      console.log('All available clays in map:', Object.keys(validCombinationsMap));
      console.log('All glaze options:', Array.from(glazeSelect.options).map(o => ({
        value: o.value,
        text: o.textContent,
        dataValue: o.dataset.optionValue
      })));
    }
    
    // Store all options if not already stored (for restoration)
    if (!glazeSelect.dataset.allOptionsStored) {
      const allOptions = Array.from(glazeSelect.options).map(opt => opt.outerHTML).join('');
      glazeSelect.dataset.allOptionsHtml = allOptions;
      glazeSelect.dataset.allOptionsStored = 'true';
    }
    
    // Remove all current options
    glazeSelect.innerHTML = '';
    
    // Get the stored HTML and parse it to restore options
    const allOptionsHtml = glazeSelect.dataset.allOptionsHtml || '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = allOptionsHtml;
    const allOptions = Array.from(tempDiv.querySelectorAll('option'));
    
    // Add back only valid options
    allOptions.forEach(option => {
      const glazeValue = option.dataset.optionValue || option.value;
      // Also check the text content in case data-option-value is missing
      // Remove " - Unavailable" suffix if present
      const glazeText = option.textContent.trim().replace(/\s*-\s*Unavailable$/i, '').trim();
      
      // Check if this glaze is valid - compare both value and text
      const isValid = validGlazes.some(validGlaze => {
        return validGlaze === glazeValue || 
               validGlaze === glazeText || 
               glazeValue === validGlaze ||
               glazeText === validGlaze;
      });
      
      if (isValid) {
        glazeSelect.appendChild(option.cloneNode(true));
      }
    });
    
    // If no valid options, add a placeholder
    if (glazeSelect.options.length === 0) {
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'No options available';
      placeholder.disabled = true;
      glazeSelect.appendChild(placeholder);
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a combined variant select (single dropdown with all combinations)
    const isCombinedSelect = event.target instanceof HTMLSelectElement && 
                             event.target.dataset.combinedVariantSelect === 'true' &&
                             event.target.name === 'id';
    
    let selectedOption;
    let variantId = null;
    
    // IMPORTANT: Check for explicitly passed variant ID first (from combined-swatch-picker)
    // This is necessary because the hidden select options may not have correct variant IDs
    // for all clay/glaze combinations when glaze names are shared across clay types.
    if (event.explicitVariantId) {
      variantId = event.explicitVariantId;
    }
    
    if (isCombinedSelect) {
      // For combined selects, the value IS the variant ID
      if (!variantId) {
        variantId = event.target.value;
      }
      selectedOption = event.target.options[event.target.selectedIndex];
    } else {
      // Normal select or input
      selectedOption = event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
    }

    if (!selectedOption) return;
    
    // Check if this is an invalid selection (e.g., "No options available" placeholder)
    if (selectedOption instanceof HTMLOptionElement) {
      if (selectedOption.disabled || selectedOption.value === '' || selectedOption.textContent.includes('No options available')) {
        console.warn('Invalid option selected, skipping variant change');
        return;
      }
    }

    this.updateSelectedOption(event.target);
    
    // Update the selected variant text display only when an actual selection is made
    // (not on hover or just opening the dropdown)
    if (event.target instanceof HTMLSelectElement) {
      // Only update if the value actually changed
      const previousValue = event.target.dataset.previousValue || '';
      if (event.target.value !== previousValue) {
        // For combined selects, update the style display with clay and glaze
        if (isCombinedSelect && selectedOption) {
          const clay = selectedOption.dataset.clay;
          const glaze = selectedOption.dataset.glaze;
          const claySpan = this.querySelector('.selection-clay');
          const glazeSpan = this.querySelector('.selection-glaze');
          if (claySpan && clay) claySpan.textContent = clay;
          if (glazeSpan && glaze) glazeSpan.textContent = glaze;
        } else {
          this.updateSelectedVariantText();
        }
        event.target.dataset.previousValue = event.target.value;
      }
    }
    
    // Get variant ID - use explicit ID if provided, otherwise get from option
    // The explicit ID takes precedence because it comes directly from the swatch
    // which has the correct variant ID even when glaze names overlap across clay types
    if (!variantId) {
      variantId = selectedOption.dataset.variantId || selectedOption.dataset.optionValueId;
    }
    
    if (!variantId) {
      console.warn('No variant ID found for selected option, skipping fetch');
      return;
    }
    
    this.dispatchEvent(new VariantSelectedEvent({ id: variantId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // For combined selects, pass the select element itself to buildRequestUrl
    // For combined swatches with explicit variant ID, pass it to ensure correct URL
    const requestTarget = isCombinedSelect ? event.target : selectedOption;
    const explicitVariantId = event.explicitVariantId || null;
    this.fetchUpdatedSection(this.buildRequestUrl(requestTarget, null, [], explicitVariantId), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string | null} [explicitVariantId] - Explicit variant ID (from combined-swatch-picker).
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], explicitVariantId = null) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    
    // Check if we're using a combined variant select (single dropdown)
    // selectedOption might be the option element or the select element itself
    const selectElement = selectedOption instanceof HTMLSelectElement ? selectedOption : selectedOption.closest?.('select');
    const isCombinedSelect = selectElement?.dataset.combinedVariantSelect === 'true';
    
    // Get product URL - for combined selects, get from variant-picker dataset, otherwise from option
    let productUrl;
    if (isCombinedSelect) {
      productUrl = this.#pendingRequestUrl || this.dataset.productUrl;
    } else {
      productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    }
    this.#pendingRequestUrl = productUrl;
    const params = [];
    
    // IMPORTANT: If we have an explicit variant ID (from combined-swatch-picker), use it directly
    // This ensures correct variant selection when glaze names are shared across clay types
    if (explicitVariantId) {
      params.push(`variant=${explicitVariantId}`);
    } else if (isCombinedSelect) {
      // For combined selects, we use variant ID directly (no option_values needed)
      const variantId = selectElement.value;
      if (variantId) {
        params.push(`variant=${variantId}`);
      }
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    
    // For product pages, we might need to include section_id to ensure proper rendering
    // Get section_id from data attribute if available
    const sectionId = this.dataset.sectionId;
    if (sectionId && productUrl) {
      // Clean the product URL (remove any existing query params)
      const cleanProductUrl = productUrl.split('?')[0];
      // Build URL with section_id and variant/option_values
      return `${cleanProductUrl}?section_id=${sectionId}&${params.join('&')}`;
    }
    
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Parse the variant data
        let variantData = null;
        try {
          variantData = JSON.parse(textContent);
        } catch (e) {
          console.error('Failed to parse variant data:', e);
          return;
        }

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          // Only dispatch if we have valid variant data
          if (this.selectedOptionId && variantData) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // Check if current variant picker is using dropdowns (has select elements)
    const currentHasDropdowns = this.querySelector('select') !== null;
    // Check if new HTML has combined swatches (different structure)
    const newHasCombinedSwatches = newHtml.querySelector('combined-swatch-picker') !== null || 
                                    newHtml.querySelector('swatches-variant-picker-component[data-combined-swatches]') !== null;
    
    // Prevent morphing if we're switching from dropdowns to combined swatches
    // This can happen if the server incorrectly returns the wrong template
    if (currentHasDropdowns && newHasCombinedSwatches) {
      console.warn('Variant picker: Server returned combined swatches but current picker uses dropdowns. Skipping morph to preserve dropdown structure.');
      // Still update product data attributes if product changed
      if (newVariantPickerSource instanceof HTMLElement) {
        const newProductId = newVariantPickerSource.dataset.productId;
        const newProductUrl = newVariantPickerSource.dataset.productUrl;
        if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
          newProduct = { id: newProductId, url: newProductUrl };
        }
        this.dataset.productId = newProductId;
        this.dataset.productUrl = newProductUrl;
      }
      // Update only the variant data/script without morphing the structure
      const newVariantScript = newVariantPickerSource.querySelector('script[type="application/json"]');
      const currentVariantScript = this.querySelector('script[type="application/json"]');
      if (newVariantScript && currentVariantScript) {
        currentVariantScript.textContent = newVariantScript.textContent;
      }
      // Update selected options in dropdowns without morphing
      if (newVariantPickerSource instanceof HTMLElement) {
        this.updateSelectedOptionsFromResponse(newVariantPickerSource);
        // Also update the swatch from the new HTML
        this.updateSwatchFromResponse(newVariantPickerSource);
      }
      return newProduct;
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    
    // After morphing, ensure the swatch is updated and re-initialize filtering
    if (newVariantPickerSource instanceof HTMLElement) {
      this.updateSwatchFromResponse(newVariantPickerSource);
      // Re-initialize option filtering after morph
      this.initializeOptionFiltering();
    }

    return newProduct;
  }

  /**
   * Updates selected options in dropdowns from the response without morphing the entire structure.
   * @param {HTMLElement} newVariantPickerSource - The new variant picker source from the response.
   */
  updateSelectedOptionsFromResponse(newVariantPickerSource) {
    // Get all select elements in current picker
    const currentSelects = Array.from(this.querySelectorAll('select'));
    const newSelects = Array.from(newVariantPickerSource.querySelectorAll('select'));
    
    // Update each select's selected option
    currentSelects.forEach((currentSelect, index) => {
      const newSelect = newSelects[index];
      if (!newSelect) return;
      
      const newSelectedOption = newSelect.querySelector('option[selected]');
      if (!newSelectedOption || !(newSelectedOption instanceof HTMLOptionElement)) return;
      
      // Find matching option in current select and select it
      const currentOption = Array.from(currentSelect.options).find(
        opt => opt.value === newSelectedOption.value
      );
      if (currentOption) {
        currentSelect.value = currentOption.value;
        // Update selected attribute
        currentSelect.querySelectorAll('option').forEach(opt => {
          opt.removeAttribute('selected');
        });
        currentOption.setAttribute('selected', 'selected');
        // Trigger change event
        currentSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    
    // Update the selected variant text display if it exists
    this.updateSelectedVariantText();
  }

  /**
   * Updates the displayed selected variant text (e.g., "Dark Clay | Glossy White") based on current dropdown selections.
   */
  updateSelectedVariantText() {
    const selectedTextElement = this.querySelector('.title-selection-value');
    if (!selectedTextElement) return;
    
    const selects = Array.from(this.querySelectorAll('select'));
    if (selects.length < 2) return;
    
    // Get selected values from dropdowns, removing " - Unavailable" suffix
    const selectedValues = selects.map(select => {
      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return '';
      let text = selectedOption.textContent.trim();
      // Remove " - Unavailable" suffix if present
      text = text.replace(/\s*-\s*Unavailable$/i, '').trim();
      return text;
    });
    
    // Update the text if we have both values and they're not empty or placeholder
    if (selectedValues.length >= 2 && selectedValues[0] && selectedValues[1] && 
        selectedValues[0] !== 'No options available' && selectedValues[1] !== 'No options available') {
      const claySpan = selectedTextElement.querySelector('.selection-clay');
      const glazeSpan = selectedTextElement.querySelector('.selection-glaze');
      
      if (claySpan && glazeSpan) {
        claySpan.textContent = selectedValues[0];
        glazeSpan.textContent = selectedValues[1];
      }
      
      // Update the swatch if it exists
      this.updateSelectedVariantSwatch(selects);
    }
  }

  /**
   * Updates the displayed swatch based on the selected variant from dropdowns.
   * @param {HTMLSelectElement[]} selects - The select elements for clay and glaze.
   */
  updateSelectedVariantSwatch(selects) {
    // The swatch will be automatically updated when the variant picker is morphed
    // with the new HTML from the server response, so we don't need to manually update it here
    // The server response includes the updated swatch HTML with the correct metaobject image
  }

  /**
   * Updates the swatch from the server response HTML.
   * @param {HTMLElement} newVariantPickerSource - The new variant picker source from the response.
   */
  updateSwatchFromResponse(newVariantPickerSource) {
    const currentSwatchContainer = this.querySelector('.variant-selection-swatch');
    const newSwatchContainer = newVariantPickerSource.querySelector('.variant-selection-swatch');
    
    if (!currentSwatchContainer || !(currentSwatchContainer instanceof HTMLElement)) return;
    if (!newSwatchContainer || !(newSwatchContainer instanceof HTMLElement)) return;
    
    // Replace the swatch HTML with the new one from the server response
    currentSwatchContainer.innerHTML = newSwatchContainer.innerHTML;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    // Check if we're using a combined variant select
    const combinedSelect = this.querySelector('select[data-combined-variant-select="true"]');
    if (combinedSelect && combinedSelect.selectedIndex >= 0) {
      // For combined selects, use variant ID from the selected option
      const selectedOption = combinedSelect.options[combinedSelect.selectedIndex];
      if (selectedOption && selectedOption.dataset.variantId) {
        return selectedOption.dataset.variantId;
      }
    }
    
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    // Check if we're using a combined variant select
    const combinedSelect = this.querySelector('select[data-combined-variant-select="true"]');
    if (combinedSelect && combinedSelect.selectedIndex >= 0) {
      // For combined selects, return empty array (we use variant ID directly, not option values)
      return [];
    }
    
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
